/* eslint-disable */
import { captureException, setUser } from '@sentry/react';
import {
  AccessGuardian,
  getAccessToken as getAuthProviderAccessToken,
  useAuth
} from '@cw-elements/auth-provider';
import { setToken } from '@cw-elements/config';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import get from 'lodash/get';
import moment from 'moment';
import { RbacAccessHoC } from 'rbac/rbacAccessHoC';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { isChrome, isMobile } from 'react-device-detect';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import { setLaunchDarklyFlags } from 'redux/actions/launchDarkly';
import styled from 'styled-components';
import { useGlobalAnalytics } from 'utils/analytics/contextual-tracking.hooks';
import { defaultLanguage, i18nToMoment } from './app.constant';
import { FooterNotification } from './components/common/footerNotification';
import { RetroLocationModal } from './components/common/modal/retroLocationModal';
import { ZendeskCustomBtn } from './components/common/zendeskWidget';
import { Loader } from './components/loader/loader';
import { RedirectionLoader } from './components/loader/redirectionLoader';
import { toThemeHoc } from './components/styles/theme';
import i18n from './i18n';
import { fetchOrgsList } from './redux/actions/orgsList';
import { fetchUserProfile, fetchUserWorkspace, openLocationModal } from './redux/actions/user';
import {
  getAccessToken,
  getLanguageCode,
  getLocaleTempUsingCookies,
  getLocaleUsingCookies,
  getWorkspaceAndOrgsSequentially,
  isAccessTokenAvailable,
  translationWrapper
} from './utils/commonMethods';
import { history, publicItems, URL_CONSTANTS } from './utils/history';
import { iff } from './utils/iff';
import { getUserLocationDetails } from './utils/ipApi';
import { useInitAnalytics } from 'utils/analytics/use-init-analytics.hook';
import { FieldsNewDeepLinkContainer } from 'app/fields/fields-new-deep-link.container';
import { FarmNewDeepLinkContainer } from './app/farms/farm-new-deep-link.container';
import { useDatadogRum } from 'app/common/hooks/use-datadog-rum.hook';

const Private = lazy(() => import('./routes/private'));
const Public = lazy(() => import('./routes/public'));

/**
 * styled root component
 */
const StyledRoot = styled.div`
  display: flex;
  flex-direction: column;
  font-family: ${(props) => get(props, 'theme.font.family')};
  height: 100%;
  min-height: 100%;
  * {
    box-sizing: border-box;
  }
`;

const App = (props) => {
  const flags = useFlags();
  const ldClient = useLDClient();
  const { user: authUser } = useAuth();
  const { t } = translationWrapper(useTranslation(), false);
  const [isLoading, setIsLoading] = useState(
    isAccessTokenAvailable() && !publicItems.includes(location.pathname)
  );
  const [hasLaunchDarklyStarted, setHasLaunchDarklyStarted] = useState(false);
  useInitAnalytics(hasLaunchDarklyStarted);
  const track = useGlobalAnalytics();
  const { setGlobalDataDD, setUserDataDD } = useDatadogRum();

  const fetchUserData = useCallback(async () => {
    if (!authUser) {
      return;
    }

    const res = await props.fetchUserProfile();
    if (res) {
      const id = res.id ?? res?.data?.id;
      const email = res.email ?? res?.data?.email;
      const name = res.name ?? res?.data?.name;
      const country = res?.country_code ?? res?.data?.country_code;
      const login = res?.login ?? res?.data?.login;

      setUser({
        id,
        email,
        name,
        language: i18n.language,
        country
      });
      setUserDataDD({
        id,
        email,
        name,
        country,
        locale: i18n.language,
        login
      });
      if (ldClient && id && email) {
        try {
          await ldClient.identify({
            kind: 'user',
            key: id,
            email,
            country: country ?? 'unknown'
          });
          setHasLaunchDarklyStarted(true);
        } catch (error) {
          captureException(error);
        }
      }
    }

    getWorkspaceAndOrgsSequentially({
      fetchUserWorkspace: props.fetchUserWorkspace,
      fetchOrgsList: props.fetchOrgsList
    });
  }, [
    authUser,
    ldClient,
    props.fetchOrgsList,
    props.fetchUserProfile,
    props.fetchUserWorkspace,
    setUserDataDD
  ]);

  useEffect(() => {
    if (!authUser) {
      return;
    }

    getUserLocationDetails();
    void fetchUserData();
  }, [authUser, fetchUserData]);

  useEffect(() => {
    if (authUser) {
      setToken(getAuthProviderAccessToken());
    }
  }, [authUser]);

  useEffect(() => {
    track('Loaded Farm Settings', { path: window.location.pathname });

    const lngByCookie =
      getLanguageCode(getLocaleTempUsingCookies()) || getLocaleUsingCookies() || defaultLanguage;
    const lng = i18nToMoment[lngByCookie] ?? lngByCookie;

    if (process.env.NODE_ENV !== 'test' && lng !== defaultLanguage) {
      import(`moment/locale/${lng}`).then().catch();
    }

    moment.locale(lng);
  }, []);

  useEffect(() => {
    setGlobalDataDD();
    const { location } = history;

    if (
      !location.pathname.includes('/app') &&
      !publicItems.includes(location.pathname) &&
      isAccessTokenAvailable()
    ) {
      history.push(URL_CONSTANTS.HALL_OF_ENTITIES(), { autoSelectSingleEntity: true });
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    props.setLaunchDarklyFlags(flags);
  }, [flags, props.setLaunchDarklyFlags]);

  if (props?.orgsError?.toString().includes('failed with status code 401')) {
    return <Loader />;
  } else {
    return (
      <StyledRoot>
        <Helmet>
          <title>{t('common.text.farm_settings')}</title>
        </Helmet>
        <Suspense
          fallback={iff(
            Object.keys(getAccessToken()).length === 0,
            <RedirectionLoader />,
            <Loader showMessage={false} />
          )}
        >
          <Routes>
            <Route
              exact
              path="/app/properties/new"
              element={
                <AccessGuardian>
                  <FarmNewDeepLinkContainer />
                </AccessGuardian>
              }
            />
            <Route
              exact
              path="/app/fields/new"
              element={
                <AccessGuardian>
                  <FieldsNewDeepLinkContainer />
                </AccessGuardian>
              }
            />
            <Route
              exact
              path="/app/*"
              element={
                <AccessGuardian>
                  <RbacAccessHoC>
                    <Private
                      history={history}
                      location={location}
                      loading={isLoading}
                      orgsError={props.orgsError}
                      orgs={props.orgs}
                    />
                  </RbacAccessHoC>
                </AccessGuardian>
              }
            />
            <Route path="/*" element={<Public />} />
          </Routes>
        </Suspense>
        <RetroLocationModal />
        <FooterNotification isChrome={isChrome} isMobile={isMobile} />
        <ZendeskCustomBtn />
      </StyledRoot>
    );
  }
};

const mapStateToProps = (state) => {
  return {
    user: state.user.userProfile,
    userSuccess: get(state, 'user.success', {}),
    orgs: get(state, 'orgsList.allOrgs.result', []),
    updatedUser: get(state, 'userAccessManagement.updatedUserDetails', {}),
    orgsLoading: get(state, 'orgsList.loading', []),
    orgsError: get(state, 'orgsList.error', null),
    orgsSuccess: get(state, 'orgsList.isFetchOrgListSuccess', false),
    isLocationModalOpen: get(state, 'isLocationModalOpen', false),
    editUserDetailsSuccess: get(state, 'userAccessManagement.editUserDetailsSuccess', false)
  };
};

const mapDispatchToProps = (dispatch) => ({
  fetchOrgsList: (queryParams) => dispatch(fetchOrgsList(queryParams)),
  fetchUserProfile: () => dispatch(fetchUserProfile()),
  fetchUserWorkspace: () => dispatch(fetchUserWorkspace()),
  setLaunchDarklyFlags: (flags) => dispatch(setLaunchDarklyFlags(flags)),
  openLocationModal: () => dispatch(openLocationModal(true))
});

export const AppLazy = connect(
  mapStateToProps,
  mapDispatchToProps
)(toThemeHoc(App, 'defaultTheme'));
